import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import FormattedText from '../components/FormattedText';
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  UserIcon,
  CalendarIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { reportsService } from '../services/api';

const ReportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadReportDetails();
  }, [id]);

  const loadReportDetails = async () => {
    try {
      setLoading(true);
      const data = await reportsService.getById(id);
      setReport(data);
    } catch (err) {
      console.error('Erro ao carregar laudo:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const openPDF = async () => {
    try {
      // Fazer download direto do backend (regenera PDF on-demand)
      const blob = await reportsService.download(id);
      const url = window.URL.createObjectURL(blob);
      
      // Abrir em nova aba
      window.open(url, '_blank');
      
      // Limpar objeto URL após um tempo
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (err) {
      console.error('Erro ao abrir PDF:', err);
      alert('Erro ao abrir PDF. Tente novamente.');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando laudo...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-800 mb-2">Erro ao carregar laudo</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/reports')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              Voltar para Laudos
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!report) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <InformationCircleIcon className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-yellow-800 mb-2">Laudo não encontrado</h2>
            <p className="text-yellow-600 mb-4">O laudo solicitado não existe ou foi removido.</p>
            <button
              onClick={() => navigate('/reports')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              Voltar para Laudos
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Parse draftJson com tratamento de erro
  let draftData = null;
  try {
    if (report.draftJson) {
      draftData = typeof report.draftJson === 'string' 
        ? JSON.parse(report.draftJson) 
        : report.draftJson;
      
    }
  } catch (parseError) {
    console.error('❌ Erro ao fazer parse do draftJson:', parseError);
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/reports')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Voltar para Laudos"
            >
              <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                <DocumentTextIcon className="w-10 h-10 text-medical-600" />
                Detalhes do Laudo
              </h1>
              <p className="text-gray-500 mt-1">Visualize as informações completas do laudo audiométrico</p>
            </div>
          </div>

          {report.pdfUrl && (
            <button
              onClick={openPDF}
              className="inline-flex items-center gap-2 px-6 py-3 bg-medical-600 text-white rounded-lg hover:bg-medical-700 transition-colors font-medium shadow-md"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              Abrir PDF
            </button>
          )}
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Patient Info */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center gap-3 mb-2">
              <UserIcon className="w-6 h-6 text-blue-500" />
              <h3 className="font-semibold text-gray-700">Paciente</h3>
            </div>
            <p className="text-xl font-bold text-gray-900">{report.patientName || 'N/A'}</p>
          </div>

          {/* Date Info */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center gap-3 mb-2">
              <CalendarIcon className="w-6 h-6 text-green-500" />
              <h3 className="font-semibold text-gray-700">Data de Geração</h3>
            </div>
            <p className="text-xl font-bold text-gray-900">{formatDate(report.createdAt)}</p>
          </div>

          {/* Status Info */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircleIcon className="w-6 h-6 text-purple-500" />
              <h3 className="font-semibold text-gray-700">Status</h3>
            </div>
            <p className="text-xl font-bold text-gray-900">{report.status || 'Draft'}</p>
          </div>
        </div>

        {/* Draft Content */}
        {draftData && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
              <DocumentTextIcon className="w-7 h-7 text-medical-600" />
              Conteúdo do Laudo
            </h2>

            {/* Validação NR7 */}
            {draftData.validacao_nr7 && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">Validação NR-7</h3>
                <div className="space-y-2 text-sm">
                  <p><span className="font-medium">Repouso 14h:</span> {draftData.validacao_nr7.repouso_14h_ok ? '✅ Sim' : '❌ Não'}</p>
                  <p><span className="font-medium">Equipamento Registrado:</span> {draftData.validacao_nr7.equipamento_registrado ? '✅ Sim' : '❌ Não'}</p>
                  {draftData.validacao_nr7.observacoes && (
                    <p className="text-gray-700"><span className="font-medium">Observações:</span> {draftData.validacao_nr7.observacoes}</p>
                  )}
                </div>
              </div>
            )}

            {/* Laudo Narrativo Completo */}
            {draftData.laudo_narrativo && (
              <div className="mb-6 p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl shadow-sm">
                <h3 className="text-xl font-bold text-green-900 mb-4 flex items-center gap-2">
                  <DocumentTextIcon className="w-6 h-6" />
                  📝 Laudo Clínico
                </h3>
                
                {/* Interpretação Técnica */}
                {draftData.laudo_narrativo.interpretacao_tecnica && (
                  <div className="mb-4 p-4 bg-white rounded-lg border border-green-200">
                    <h4 className="font-semibold text-green-800 mb-2">🔍 Interpretação Técnica:</h4>
                    <FormattedText text={draftData.laudo_narrativo.interpretacao_tecnica} className="text-gray-800" />
                  </div>
                )}

                {/* Conclusão Ocupacional */}
                {draftData.laudo_narrativo.conclusao_ocupacional && (
                  <div className="mb-4 p-4 bg-white rounded-lg border border-green-200">
                    <h4 className="font-semibold text-green-800 mb-2">💼 Conclusão Ocupacional:</h4>
                    <FormattedText text={draftData.laudo_narrativo.conclusao_ocupacional} className="text-gray-800" />
                  </div>
                )}

                {/* Conclusão Final */}
                {draftData.laudo_narrativo.conclusao_final && (
                  <div className="mb-4 p-4 bg-white rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-2">✅ Conclusão Final:</h4>
                    <FormattedText text={draftData.laudo_narrativo.conclusao_final} className="text-gray-800 font-semibold" />
                  </div>
                )}

                {/* Recomendações */}
                {draftData.laudo_narrativo.recomendacoes && (
                  <div className="p-4 bg-white rounded-lg border border-green-200">
                    <h4 className="font-semibold text-green-800 mb-2">💡 Recomendações:</h4>
                    <FormattedText text={draftData.laudo_narrativo.recomendacoes} className="text-gray-800" />
                  </div>
                )}
              </div>
            )}

            {/* Portaria 19 */}
            {draftData.interpretacao_portaria19 && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="font-semibold text-green-900 mb-3">Interpretação Portaria 19</h3>
                {draftData.interpretacao_portaria19.exames?.map((exame, index) => (
                  <div key={index} className="mb-3 p-3 bg-white rounded border border-green-200">
                    <p className="text-sm"><span className="font-medium">Classificação:</span> <span className="font-bold text-green-700">{exame.classificacao}</span></p>
                    <p className="text-sm"><span className="font-medium">OD Média (3-6kHz):</span> {exame.od_media_3a6khz_db} dB</p>
                    <p className="text-sm"><span className="font-medium">OE Média (3-6kHz):</span> {exame.oe_media_3a6khz_db} dB</p>
                  </div>
                ))}
              </div>
            )}

            {/* Nexo Causal */}
            {draftData.nexo_causal && (
              <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <h3 className="font-semibold text-purple-900 mb-3">Nexo Causal</h3>
                <p className="mb-3"><span className="font-medium">Classificação:</span> <span className="font-bold text-purple-700 uppercase">{draftData.nexo_causal.classificacao}</span></p>
                {draftData.nexo_causal.fundamentos && (
                  <div className="mt-3">
                    <p className="font-medium text-sm text-purple-800 mb-2">Fundamentos:</p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                      {draftData.nexo_causal.fundamentos.map((fund, index) => (
                        <li key={index}>{fund}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Dados Faltantes */}
            {draftData.faltantes_criticos && draftData.faltantes_criticos.length > 0 && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h3 className="font-semibold text-yellow-900 mb-2">⚠️ Dados Faltantes Críticos</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-yellow-800">
                  {draftData.faltantes_criticos.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Raw JSON (for debugging) */}
        <details className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <summary className="font-semibold text-gray-700 cursor-pointer hover:text-medical-600">
            Ver JSON Completo (Desenvolvedor)
          </summary>
          <pre className="mt-4 p-4 bg-gray-900 text-green-400 rounded-lg text-xs overflow-x-auto">
            {JSON.stringify(draftData, null, 2)}
          </pre>
        </details>
      </div>
    </DashboardLayout>
  );
};

export default ReportDetail;
